import {
  assign as lodashAssign,
  clone,
  forEach,
  filter,
  find,
  findIndex,
  findKey,
  get,
  includes,
  isArray,
  isNil,
  isString,
  merge,
  pickBy,
  set,
} from "lodash-es";
import { createStore, applyMiddleware } from "redux";
import { bindAll, createAction, createReducer } from "redux-act";
import { createLogger } from "redux-logger";
import { thunk as thunkMiddleware } from "redux-thunk";
import { api } from "../util/api";

// Logger
const logger = createLogger();

// Actions
export const addField = createAction("add a new field");
export const removeField = createAction("remove a field");
export const updateField = createAction("update a field");
export const selectField = createAction("select a field");
export const updateFieldOrder = createAction("update order of a field");

export const addCustomField = createAction("add custom field");
export const updateCustomField = createAction("update custom field");
export const removeCustomField = createAction("remove custom field");

export const getSchema = createAction("get schema");
export const getSchemaError = createAction("get schema error");
export const getSchemaSuccess = createAction("get schema complete");

export const updateCurrentPage = createAction("update current page");
export const updatePageDefinition = createAction("update page bounds");

export const save = createAction("save");
export const saveError = createAction("save error");
export const saveSuccess = createAction("save complete");

export const preview = createAction("preview");

// Reducers
const INITIAL_STATE = {
  selectedField: null,
  pageImages: [],
  pageSizes: [],
  fields: [],
  customFields: {},
  defaultFields: {},
  saving: false,
  loading: true,
  dirty: false,
  currentPage: 0,
  pageDefinitions: [],
};

export const fieldReducer = createReducer(
  {
    [selectField]: (s, p) => {
      return merge({}, s, { selectedField: p.fieldId });
    },
    [addField]: (s, p) => {
      s.fields.push(p.data);
      return merge({}, s, { dirty: true });
    },
    [removeField]: (s, p) => {
      const { fieldId } = p;
      const fieldIndex = findIndex(s.fields, (f) => f.id === fieldId);

      if (!isNil(fieldIndex)) {
        s.selectField = null;
        s.fields.splice(fieldIndex, 1);
      }

      return merge({}, s, { dirty: true });
    },
    [updateField]: (s, p) => {
      const { fieldId, data, assign = false } = p;
      const fieldIndex = findIndex(s.fields, (f) => f.id === fieldId);

      if (!isNil(fieldIndex)) {
        const oldField = s.fields[fieldIndex];
        let newField;

        if (assign) {
          newField = lodashAssign({}, oldField, data);
        } else {
          newField = merge({}, oldField, data);
        }

        s.fields.splice(fieldIndex, 1, newField);
      }

      return merge({}, s, { dirty: true });
    },
    [updateFieldOrder]: (s, p) => {
      const { fieldList } = p;

      forEach(fieldList, (field, index) => {
        const fieldIndex = findIndex(s.fields, (f) => f.id === field.id);
        if (fieldIndex >= 0) {
          const oldField = s.fields[fieldIndex];
          const newField = lodashAssign({}, oldField, { fieldOrder: index });
          s.fields.splice(fieldIndex, 1, newField);
        }
      });

      return merge({}, s, { dirty: true });
    },
    [addCustomField]: (s, p) => {
      const { key, data } = p;
      set(s.customFields, key, data);
      return merge({}, s, { dirty: true });
    },
    [removeCustomField]: (s, p) => {
      const { fieldId } = p;
      const fieldKey = findKey(s.customFields, (f) => f.id === fieldId);

      if (!isNil(fieldKey)) {
        // Remove custom field
        delete s.customFields[fieldKey];

        // Remove all custom field references
        forEach(s.fields, (f) => {
          // Continue if empty
          if (!f.field) return;

          if (isString(f.field)) {
            // Set to an empty array
            if (includes(f.field, fieldKey)) {
              f.field = [];
            }
          } else if (isArray(f.field)) {
            // Set only to values that do not contain the field key
            f.field = filter(f.field, (j) => !includes(j, fieldKey));
          }
        });
      }

      // Return a new state object
      return merge({}, s, { dirty: true });
    },
    [updateCustomField]: (s, p) => {
      // Mirrors updateField
      const { fieldId, data, assign = false } = p;
      const fieldKey = findKey(s.customFields, (f) => f.id === fieldId);

      if (!isNil(fieldKey)) {
        const oldField = s.customFields[fieldKey];
        let newField;
        let newCustomFields;
        if (assign) {
          newField = lodashAssign({}, oldField, data);
          newCustomFields = lodashAssign({}, s.customFields, {
            [fieldKey]: newField,
          });
        } else {
          newField = merge({}, oldField, data);
          newCustomFields = merge({}, s.customFields, {
            [fieldKey]: newField,
          });
        }

        s.customFields = newCustomFields;
      }

      return merge({}, s, { dirty: true });
    },
    [save]: (s) => {
      return merge({}, s, { saving: true });
    },
    [saveSuccess]: (s) => {
      return merge({}, s, { saving: false, dirty: false });
    },
    [saveError]: (s) => {
      return merge({}, s, { saving: false });
    },
    [preview]: (s, p) => {
      return merge({}, s, { preview: p });
    },
    [getSchema]: (s) => merge({}, s, { loading: true }),
    [getSchemaSuccess]: (s, schema) => merge({}, s, schema, { loading: false }),
    [getSchemaError]: (s) => merge({}, s, { loading: false }),
    [updateCurrentPage]: (s, p) => {
      const { newPage } = p;
      return merge({}, s, { currentPage: newPage });
    },
    [updatePageDefinition]: (s, p) => {
      const { pageIndex, page } = p;
      const { pageDefinitions } = s;
      const newDefinitions = clone(pageDefinitions);

      newDefinitions[pageIndex] = page;

      return merge({}, s, { pageDefinitions: newDefinitions });
    },
  },
  INITIAL_STATE
);

// Async Actions

const SAVE_KEYS = ["customFields", "fields"];
export const saveDocument = (updatePath) => {
  return (dispatch, getState) => {
    const state = getState();
    const saveObj = pickBy(state, (_v, k) => includes(SAVE_KEYS, k));

    dispatch(save());
    api
      .post(updatePath, { schema: saveObj })
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          dispatch(saveSuccess());
        } else {
          dispatch(saveError());
        }
      })
      .catch((e) => {
        if (e.response) {
          console.error(e);
        }

        dispatch(saveError());
      });
  };
};

export const previewSchema = () => {
  return (dispatch, getState) => {
    const state = getState();
    const saveObj = pickBy(state, (_v, k) => includes(SAVE_KEYS, k));
    dispatch(preview(saveObj));
  };
};

export const getDocument = (getPath) => {
  return (dispatch) => {
    dispatch(getSchema());
    api
      .get(getPath)
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          dispatch(getSchemaSuccess(res.data));
        } else {
          dispatch(getSchemaError());
        }
      })
      .catch((e) => {
        if (e.response) {
          console.error(e);
        }

        dispatch(saveError());
      });
  };
};

// Selectors

export const selectFieldsByPageNumber = (state, num) => {
  const fields = get(state, "fields", {});

  return filter(fields, (f) => {
    const pageNum = get(f, "position.page", 0);
    return pageNum === num;
  });
};

export const selectFieldById = (state, id) => {
  const fields = get(state, "fields", {});

  return find(fields, (f) => {
    return f.id === id;
  });
};

// Store
export const fieldStore = createStore(fieldReducer, applyMiddleware(thunkMiddleware, logger));

bindAll([addField, removeField, updateField], fieldStore);
